import * as Yup from 'yup';
import { useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { useAppDispatcher } from 'src/redux/store';
import { adjustPackagingItemQuantity } from 'src/redux/slices/packaging-item';

import FormProvider, { RHFTextField } from 'src/components/hook-form';

import { IPackagingItemListItem } from 'src/types/packaging-item';

type Props = {
  item: Pick<IPackagingItemListItem, '_id' | 'itemName' | 'quantity' | 'unit'>;
  open: boolean;
  onClose: VoidFunction;
  onSuccess?: VoidFunction;
};

export default function PackagingItemAdjustQuantityDialog({
  item,
  open,
  onClose,
  onSuccess,
}: Props) {
  const dispatch = useAppDispatcher();

  const unit = item.unit?.abbreviation ?? '';

  const schema = useMemo(
    () =>
      Yup.object().shape({
        newQuantity: Yup.number()
          .typeError('Enter the new quantity')
          .required('New quantity is required')
          .min(0, 'Quantity cannot be negative')
          .notOneOf([item.quantity], 'Enter a quantity different from the current one'),
        reason: Yup.string().max(500, 'Keep the reason under 500 characters').optional(),
      }),
    [item.quantity]
  );

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: { newQuantity: item.quantity, reason: '' },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { isSubmitting },
  } = methods;

  const watched = useWatch({ control, name: 'newQuantity' });
  const newQuantity = Number(watched);
  const hasNew = watched !== undefined && `${watched}` !== '' && !Number.isNaN(newQuantity);
  const difference = hasNew ? Number((newQuantity - item.quantity).toFixed(3)) : 0;

  const handleClose = () => {
    reset({ newQuantity: item.quantity, reason: '' });
    onClose();
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      await dispatch(
        adjustPackagingItemQuantity({
          itemId: item._id,
          newQuantity: Number(data.newQuantity),
          reason: data.reason?.trim() || undefined,
        })
      ).unwrap();

      enqueueSnackbar('Quantity adjusted successfully');
      onSuccess?.();
      handleClose();
    } catch (e) {
      enqueueSnackbar(typeof e === 'string' ? e : e?.message || 'Failed to adjust quantity', {
        variant: 'error',
      });
    }
  });

  return (
    <Dialog fullWidth maxWidth="xs" open={open} onClose={handleClose}>
      <DialogTitle sx={{ pb: 2 }}>Adjust Quantity</DialogTitle>

      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogContent sx={{ typography: 'body2' }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            {item.itemName}
          </Typography>

          <Stack spacing={2}>
            <RHFTextField
              name="newQuantity"
              label="New Quantity"
              type="number"
              required
              autoFocus
              InputProps={{ endAdornment: unit }}
            />

            <Box sx={{ p: 2, bgcolor: 'background.neutral', borderRadius: 1 }}>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Before
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {item.quantity} {unit}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    After
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {hasNew ? `${newQuantity} ${unit}` : '-'}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Change
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color={(difference >= 0 && 'success.main') || 'error.main'}
                  >
                    {hasNew ? `${difference >= 0 ? '+' : ''}${difference} ${unit}` : '-'}
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            <RHFTextField
              name="reason"
              label="Reason (Optional)"
              multiline
              rows={2}
              placeholder="e.g., Recount, damaged stock found, etc."
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={handleClose}>
            Cancel
          </Button>

          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            Adjust
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
